// CartDataContext.jsx
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useLocation } from 'react-router-dom';

import { intervalToDuration } from 'date-fns';
import useTimeslotApi from '@/components/hooks/useTimeslotApi';
import {
  TimeSlotExpiredDialog,
  TimeSlotExtendDialog,
} from '../Components/TimeSlotExtendDialog';
import useFetch from 'use-http';
import { set } from 'lodash';
import { useRegistration } from './RegistrationContext';

const CartDataContext = createContext(null);

const SESSION_DURATION_MIN = 20;
const SHOW_ALERT_AT_MIN = 5;

export function CartDataProvider({ children }) {
  const [services, setServices] = useState([]);
  const [complex, setComplex] = useState(null);
  const [complexPrice, setComplexPrice] = useState(null);
  const [additionalServices, setAdditionalServices] = useState([]);
  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otherServices, setOtherServices] = useState([]);

  const [questions, setQuestions] = useState(null);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [questionsError, setQuestionsError] = useState(null);

  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);

  const [timeslotReservedUntil, setTimeslotReservedUntil] = useState(
    new Date(Date.now() + SESSION_DURATION_MIN * 60 * 1000).toISOString(),
  );

  const [remainingTimeText, setRemainingTimeText] = useState(null);
  const [showExtendModal, setShowExtendModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [pathname, setPathname] = useState();

  const { request: timeRequest } = useFetch(`/TimeSlots`);
  const [sessionApiLoading, setSessionApiLoading] = useState(false);
  const [addingMoreServices, setAddingMoreServices] = useState(false);
  const [addingMorePsdfServices, setAddingMorePsdfServices] = useState(false);

  // 📝 Simple logger helper
  const logState = (name, value) => {
    //console.log(`[CartData] ${name}:`, value);
  };

  // 🔍 Track state changes
 // useEffect(() => logState('coupon', coupon), [coupon]);
  // useEffect(
  //   () => logState('additionalServices', additionalServices),
  //   [additionalServices],
  // );

  // useEffect(() => logState('complex', complex), [complex]);
  // useEffect(() => logState('complexPrice', complexPrice), [complexPrice]);

  useEffect(() => {
    const allowedPaths = [
      '/cart',
      '/selfservice',
      '/login/smartid',
      '/account',
      '/login/msignature',
      '/confirmIdentity',
      '/verify/phone',
      '/register',
      '/register/info',
      '/dovanu-kuponai/cart',
      '/kompleksines-paslaugos/cart',
    ];

    const run = async () => {
      
      if (pathname && !allowedPaths.some((path) => pathname?.startsWith(path))) {
        try {
          const slotId = selectedTimeSlot?.timeSlotId;
          if (slotId) {
            await cancelTimeSlot(slotId);
          }
        } finally {
          // console.log('reseting', pathname)
          resetCart();
        }
      }
    };

    run();
  }, [pathname]);

  const resetCart = async () => {
    setServices([]);
    setOtherServices(null);
    setQuestions(null);
    setQuestionsError(null);
    setSelectedTimeSlot(null);
    setTimeslotReservedUntil('');
    setRemainingTimeText(null);
    setShowExtendModal(false);
    setShowExpiredModal(false);
    setAdditionalServices([]);
    setComplex(null);
    setComplexPrice(null);
    setAddingMoreServices(false);
    setAddingMorePsdfServices(false);
    setCoupon(null);
  };

  const cancelTimeSlot = async (slotId) => {
    try {
      await timeRequest
        .delete(
          `/Reservation/${slotId}?additionalData=triggeredAt:useTimeslotApi.js removeServices; historyLocation:,`,
        )
        .catch(() => {});
    } catch (err) {
      console.error('Failed to cancel reservation', err);
    }
  };

  useEffect(() => {
    if (selectedTimeSlot?.startDate) {
      setTimeslotReservedUntil(
        new Date(Date.now() + SESSION_DURATION_MIN * 60 * 1000).toISOString(),
      );
    }
  }, [selectedTimeSlot?.startDate]);

  useEffect(() => {
    if (!timeslotReservedUntil || !selectedTimeSlot?.startDate) {
      setRemainingTimeText('');
      return;
    }

    let expired = false;
    let id;

    const tick = () => {
      const timeslotReservedDate = new Date(timeslotReservedUntil);
      const selectedStartDate = new Date(selectedTimeSlot.startDate);
      const now = new Date();

      const availableUntil =
        timeslotReservedDate < selectedStartDate
          ? timeslotReservedDate
          : selectedStartDate;

      const untilMs = Math.max(availableUntil - now, 0);
      const diffSec = Math.floor(untilMs / 1000);
      const mm = Math.floor(diffSec / 60);
      const ss = diffSec % 60;
      setRemainingTimeText(
        `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`,
      );

      if (diffSec > 0 && mm <= SHOW_ALERT_AT_MIN) {
        setShowExtendModal(true);
      } else {
        setShowExtendModal(false);
      }
      if (diffSec <= 0 && !expired) {
        expired = true;
        setShowExpiredModal(true);
        clearInterval(id);
        handleCancel();
      }
    };

    tick();
    id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [timeslotReservedUntil, selectedTimeSlot]);

  // 🆕 modal handlers
  const handleExtend = async () => {
    try {
      setSessionApiLoading(true);
      await extendTimeSlot();
      setShowExtendModal(false);
    } catch {
    } finally {
      setSessionApiLoading(false);
    }
  };
  const handleCancel = async () => {
    try {
      setSessionApiLoading(true);
      await cancelTimeSlot(selectedTimeSlot?.timeSlotId);
      await resetCart();
      setShowExtendModal(false);
      setShowExpiredModal(true);
    } catch {
    } finally {
      setSessionApiLoading(false);
    }
  };

  const extendTimeSlot = async () => {
    const timeSlotsIds = [selectedTimeSlot.timeSlotId];
    await timeRequest
      .put('/Reservation', {
        timeSlotsIds,
        additionalData: `triggeredAt:cartDataContext.js extendTime;`,
      })
      .then(() => {
        setTimeslotReservedUntil(
          new Date(Date.now() + SESSION_DURATION_MIN * 60 * 1000).toISOString(),
        );
      });
  };

  const value = useMemo(
    () => ({
      service: services?.[0] ?? null, // READ-ONLY alias for legacy code
      services,
      setServices,
      loading,
      setLoading,

      otherServices,
      setOtherServices,

      questions,
      setQuestions,
      questionsLoading,
      setQuestionsLoading,
      questionsError,
      setQuestionsError,

      selectedTimeSlot,
      setSelectedTimeSlot,
      coupon,
      setCoupon,

      timeslotReservedUntil,
      setTimeslotReservedUntil,
      resetCart,
      remainingTimeText,
      showExtendModal,
      showExpiredModal,
      setShowExpiredModal,
      handleExtend,
      handleCancel,

      sessionApiLoading,
      additionalServices,
      setAdditionalServices,

      complex,
      setComplex,
      complexPrice,
      setComplexPrice,
      pathname,
      setPathname,
      addingMoreServices,
      setAddingMoreServices,
      addingMorePsdfServices,
      setAddingMorePsdfServices,
    }),
    [
      services,
      loading,
      otherServices,
      questions,
      questionsLoading,
      questionsError,
      selectedTimeSlot,
      coupon,
      timeslotReservedUntil,
      remainingTimeText,
      showExtendModal,
      showExpiredModal,
      sessionApiLoading,
      additionalServices,
      complex,
      complexPrice,
      pathname,
      addingMoreServices,
      addingMorePsdfServices,
    ],
  );

  return (
    <CartDataContext.Provider value={value}>
      {children}
      <TimeSlotExtendDialog />
      <TimeSlotExpiredDialog />
    </CartDataContext.Provider>
  );
}

export const useCartData = () => {
  const ctx = useContext(CartDataContext);
  if (!ctx) throw new Error('useCartData must be used within CartDataProvider');
  return ctx;
};
